import { Button, Card, Input, Tabs, Select, Table, message, Pagination } from "antd";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import ExportModal from "../components/exportModal";
import TransliterateModal from "../components/transliterateModal";
import { useAppContext } from "@/components/AppContext";
import { A4B_APPS } from "@/utils/constants";
import AdminBreadcrumb from "@/ui/organisms/AdminBreadcrumb";
import { showErrorToast } from "@/utils/helper";

const Container = styled(Card)`
  display: flex;
  flex-direction: column;
  width: 100%;

  .ant-card-body {
    flex-grow: 1;
  }
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    width: 100%;
  }
  
  .ant-tabs-nav-list {
    width: 100%;
    display: flex;
  }
  
  .ant-tabs-tab {
    flex: 1;
    justify-content: center;
    margin: 0 !important;
  }

  .ant-tabs-tab-active {
    background-color: #1890FF33 !important;
  }
`;

const { Search } = Input;
const { Option } = Select;

interface MemberDetail {
    full_name: string;
    default_name: string;
}

interface SankalpDetail {
    order_id: string;
    booking_id: string;
    sku_name: string;
    mobile_number: string;
    gotra: string;
    default_gotra: string;
    member_details: MemberDetail[];
}

interface Inventory {
    id: string;
    name: string;
    type: 'DOMESTIC' | 'GLOBAL';
    inventory_type: 'pooja' | 'item';
    transliterated_languages?: string[];
    batch_ids?: string[];
    state?: 'PROCESSING' | 'SUCCESS' | 'FAILED' | 'ENQUEUED';
}

interface StoreDetailsResponse {
    data: {
        id: string;
        store_name: string;
        store_type?: string; // Added store_type
        inventories: Inventory[];
    };
}

interface BatchInventoryResponse {
    data: {
        transliterated_language: string;
        batch_code?: string;
        sankalp_details: SankalpDetail[];
    };
}

interface TableDataType {
    key: string;
    sno: number;
    order_id: string;
    booking_id: string;
    sku_name: string;
    mobile_number: string;
    gotra: string;
    default_gotra: string;
    isEditing?: boolean;
    editedGotra?: string;
    editedMemberNames?: { [key: string]: string };
    [key: string]: any;
}

const SankalpSheetDetails = () => {
    const [searchParams] = useSearchParams();
    const storeId = searchParams.get("store_id");

    const [selectedButtons, setSelectedButtons] = useState<Record<string, string>>({});
    const [selectedLanguages, setSelectedLanguages] = useState<Record<string, string>>({});
    const [searchValue, setSearchValue] = useState("");
    const [tableData, setTableData] = useState<TableDataType[]>([]);
    const [tableColumns, setTableColumns] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState<Record<string, number>>({});
    const [totalPages, setTotalPages] = useState<Record<string, number>>({});
    const [isExportModalVisible, setIsExportModalVisible] = useState(false);
    const [exportFormat, setExportFormat] = useState<"PDF" | "XLSX">("XLSX");
    const [exportLanguage, setExportLanguage] = useState("default");
    const [selectedInventories, setSelectedInventories] = useState<Record<string, boolean>>({});
    const [activeTab, setActiveTab] = useState<string>("DOMESTIC");
    const [isTransliterateModalVisible, setIsTransliterateModalVisible] = useState(false);
    const [availableLanguagesForTransliteration, setAvailableLanguagesForTransliteration] = useState<string[]>([]);
    const [selectedLanguageForTransliteration, setSelectedLanguageForTransliteration] = useState("");
    const [storeDetails, setStoreDetails] = useState<StoreDetailsResponse | null>(null);
    const [showAllItems, setShowAllItems] = useState(false);
    const { networkInstance, app } = useAppContext();

    const monetisationPujaApi = app === A4B_APPS.SRIMANDIR
        ? networkInstance.clientWithHeaders.monetisationPujaApi
        : networkInstance.clientWithHeaders.monetisationAstroPujaApi;

    // Group inventories by type
    const groupedByType = useMemo(() => {
        return storeDetails?.data?.inventories?.reduce<Record<string, Inventory[]>>((acc, curr) => {
            if (!acc[curr.type]) {
                acc[curr.type] = [];
            }
            acc[curr.type].push(curr);
            return acc;
        }, {}) || {};
    }, [storeDetails]);

    // Get selected inventory for a type
    const getSelectedInventory = useCallback((type: string) => {
        const items = groupedByType[type] || [];
        return items.find(item => item.id === selectedButtons[type]);
    }, [groupedByType, selectedButtons]);

    // Update the generateColumns function
    const generateColumns = useCallback((sankalpDetails: SankalpDetail[], language: string) => {
        const isPitruStore = storeDetails?.data?.store_type === 'PITRU_ANCESTOR';
        const maxMembers = Math.max(...sankalpDetails.map(detail => detail.member_details.length), 0);

        const regularMemberTitles = [
            'First Member Name', 'Second Member Name', 'Third Member Name', 'Fourth Member Name',
            'Fifth Member Name', 'Sixth Member Name', 'Seventh Member Name', 'Eighth Member Name',
            'Ninth Member Name', 'Tenth Member Name'
        ];

        const pitruMemberTitles = [
            "Karta's Name", 'Pitru Member 1', 'Pitru Member 2', 'Pitru Member 3', 'Pitru Member 4',
            'Pitru Member 5', 'Pitru Member 6', 'Pitru Member 7', 'Pitru Member 8', 'Pitru Member 9'
        ];

        const memberTitles = isPitruStore ? pitruMemberTitles : regularMemberTitles;

        const baseColumns = [
            { title: 'S.No', dataIndex: 'sno', key: 'sno', width: 60, fixed: 'left' as const },
            { title: 'Order ID', dataIndex: 'order_id', key: 'order_id', width: 200 },
            { title: 'Booking ID', dataIndex: 'booking_id', key: 'booking_id', width: 120 },
            { title: 'SKU Name', dataIndex: 'sku_name', key: 'sku_name', width: 120 },
            { title: 'Mobile Number', dataIndex: 'mobile_number', key: 'mobile_number', width: 150 }
        ];

        // Add gotra columns
        const gotraColumns = [];
        if (language !== 'default') {
            gotraColumns.push({
                title: isPitruStore ? 'Default Gotra of Karta' : 'Default Gotra',
                dataIndex: 'default_gotra',
                key: 'default_gotra',
                width: 150,
            });
        }
        gotraColumns.push({
            title: isPitruStore ? 'Gotra of Karta' : 'Gotra',
            dataIndex: 'gotra',
            key: 'gotra',
            width: 150,
            render: (text: string, record: TableDataType) => (
                record.isEditing && language !== 'default' ? (
                    <Input
                        value={record.editedGotra !== undefined ? record.editedGotra : text}
                        onChange={(e) => handleEditFieldChange(record.key, 'editedGotra', e.target.value)}
                    />
                ) : text
            ),
        });

        const memberColumns = [];
        for (let i = 0; i < maxMembers && i < memberTitles.length; i++) {
            if (language !== 'default') {
                memberColumns.push({
                    title: `Default ${memberTitles[i]}`,
                    dataIndex: `default_member_${i}`,
                    key: `default_member_${i}`,
                    width: 200,
                    render: (text: string) => text || '-',
                });
            }
            memberColumns.push({
                title: memberTitles[i],
                dataIndex: `member_${i}`,
                key: `member_${i}`,
                width: 200,
                render: (text: string, record: TableDataType) => (
                    record.isEditing && language !== 'default' ? (
                        <Input
                            value={record.editedMemberNames?.[`member_${i}`] !== undefined
                                ? record.editedMemberNames[`member_${i}`] : text}
                            onChange={(e) => handleEditFieldChange(record.key, `editedMemberNames.member_${i}`, e.target.value)}
                        />
                    ) : (text || '-')
                ),
            });
        }

        return [...baseColumns, ...gotraColumns, ...memberColumns];
    }, [storeDetails]); // Added storeDetails dependency

    const transformDataToTable = useCallback((sankalpDetails: SankalpDetail[], language: string): TableDataType[] => {
        return sankalpDetails.map((detail, index) => {
            const row: TableDataType = {
                key: `${detail.order_id}-${index}`,
                sno: index + 1,
                order_id: detail?.order_id,
                booking_id: detail?.booking_id,
                sku_name: detail?.sku_name,
                mobile_number: detail?.mobile_number,
                gotra: detail?.gotra,
                default_gotra: detail?.default_gotra,
                isEditing: false,
                editedGotra: detail?.gotra, // Initialize with original value
                editedMemberNames: {}
            };

            detail?.member_details?.forEach((member, memberIndex) => {
                if (language !== 'default') {
                    row[`default_member_${memberIndex}`] = member.default_name;
                }
                row[`member_${memberIndex}`] = member.full_name;
                row.editedMemberNames = {
                    ...row.editedMemberNames,
                    [`member_${memberIndex}`]: member.full_name // Initialize with original value
                };
            });

            return row;
        });
    }, []);

    const fetchBatchInventoryData = useCallback(async (inventoryId: string, batchId: string, language: string, bookingId?: string) => {
        try {
            const response = await monetisationPujaApi?.sankalpSheetApi?.getBatchInventoryData(
                inventoryId,
                {
                    language_code: language || 'default',
                    batch_id: batchId,
                    booking_id: bookingId
                }
            );
            return response?.data;
        } catch (error) {
            console.error('Error fetching batch inventory data:', error);
            throw error;
        }
    }, [monetisationPujaApi]);

    const [currentBatchDetails, setCurrentBatchDetails] = useState<{
        batchId: string | null;
        itemCount: number;
    } | null>(null);

    const loadTableData = useCallback(async (type: string, bookingId?: string) => {
        const selectedInventory = getSelectedInventory(type);
        if (!selectedInventory) {
            setTableData([]);
            setTableColumns([]);
            setCurrentBatchDetails(null);
            setTotalPages(prev => ({ ...prev, [type]: 0 }));
            return;
        }

        const currentPageNum = currentPage[type] || 1;
        const batchId = selectedInventory.inventory_type === 'pooja'
            ? selectedInventory.batch_ids?.[currentPageNum - 1] || null
            : null;
        const language = selectedLanguages[type];

        setLoading(true);
        try {
            let response
            if ((selectedInventory.inventory_type === 'pooja' && batchId !== null) || selectedInventory.inventory_type === 'item')
                response = await fetchBatchInventoryData(
                    selectedInventory.id,
                    batchId!,
                    language,
                    bookingId
                );

            if (!response?.data?.sankalp_details) {
                throw new Error('No data received');
            }

            const columns = generateColumns(response?.data?.sankalp_details, language);
            const tableRows = transformDataToTable(response?.data?.sankalp_details, language);

            setTableColumns(columns);
            setTableData(tableRows);
            setCurrentBatchDetails({
                batchId,
                itemCount: tableRows?.length
            });

            const totalPagesCount = selectedInventory.inventory_type === 'pooja'
                ? selectedInventory?.batch_ids?.length || 0
                : 1;

            setTotalPages((prev: any) => ({
                ...prev,
                [type]: totalPagesCount
            }));
        } catch (error) {
            console.error('Error loading table data:', error);
            setTableData([]);
            setTableColumns([]);
            setCurrentBatchDetails(null);
            setTotalPages(prev => ({ ...prev, [type]: 0 }));
        } finally {
            setLoading(false);
        }
    }, [currentPage, fetchBatchInventoryData, generateColumns, getSelectedInventory, selectedLanguages, transformDataToTable]);

    const handleSearch = useCallback((value: string) => {
        setSearchValue(value);
        loadTableData(activeTab, value);
    }, [activeTab, loadTableData]);

    useEffect(() => {
        const fetchStoreDetails = async () => {
            if (!storeId) return;

            setLoading(true);
            try {
                const response = await monetisationPujaApi?.sankalpSheetApi?.getSankalpStoreDetails(storeId);
                setStoreDetails(response?.data);

                const initialSelected: Record<string, boolean> = {};
                response?.data?.data?.inventories?.forEach((inv: any) => {
                    initialSelected[inv.id] = false;
                });
                setSelectedInventories(initialSelected);
            } catch (error) {
                showErrorToast(error)
                //message.error('Failed to load store details');
                console.error('Error loading store details:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchStoreDetails();
    }, [storeId, monetisationPujaApi]);

    useEffect(() => {
        if (!storeDetails) return;

        const initialSelection: Record<string, string> = {};
        const initialLanguages: Record<string, string> = {};
        const initialPages: Record<string, number> = {};

        Object.entries(groupedByType).forEach(([type, items]) => {
            if (items.length > 0) {
                initialSelection[type] = items[0].id;
                initialPages[type] = 1;
            }
        });

        setSelectedButtons(initialSelection);
        setSelectedLanguages(initialLanguages);
        setCurrentPage(initialPages);
    }, [storeDetails, groupedByType]);

    useEffect(() => {
        if (activeTab && selectedButtons[activeTab]) {
            loadTableData(activeTab);
        }
    }, [activeTab, selectedButtons, selectedLanguages, currentPage, loadTableData]);

    const handleExport = useCallback(() => {
        setIsExportModalVisible(true);
    }, []);

    const getLanguageDisplayName = useCallback((code: string) => {
        const languageMap: Record<string, string> = {
            'hi': 'Hindi', 'en': 'English', 'bn': 'Bengali', 'ta': 'Tamil', 'te': 'Telugu', 'kn': 'Kannada'
        };
        return languageMap[code] || code;
    }, []);

    const handleTranslate = useCallback(() => {
        const selectedInventory = getSelectedInventory(activeTab);
        if (!selectedInventory) return;

        const allLanguages = ['hi', 'en', 'bn', 'ta', 'te', 'kn'];
        const existingLanguages = selectedInventory.transliterated_languages || [];
        const missingLanguages = allLanguages.filter(lang => !existingLanguages.includes(lang));

        if (missingLanguages.length === 0) {
            message.info('All available languages are already transliterated');
            return;
        }

        setAvailableLanguagesForTransliteration(allLanguages);
        setSelectedLanguageForTransliteration(allLanguages[0]);
        setIsTransliterateModalVisible(true);
    }, [activeTab, getSelectedInventory]);

    const transliterateInventory = useCallback(async (inventoryId: string, languageCode: string, inventoryTypes: string[]) => {
        try {
            await monetisationPujaApi?.sankalpSheetApi?.transliterateInventory(
                storeId!,
                {
                    language_code: languageCode,
                    inventory_type: inventoryTypes
                }
            );
            return true;
        } catch (error) {
            console.error('Error during transliteration:', error);
            throw error;
        }
    }, [monetisationPujaApi, storeId]);

    const handleTransliterate = useCallback(async (inventoryTypes: string[]) => {
        if (!selectedLanguageForTransliteration || inventoryTypes.length === 0) return;

        setLoading(true);
        setIsTransliterateModalVisible(false);

        try {
            const selectedInventory = getSelectedInventory(activeTab);
            if (!selectedInventory) throw new Error('No inventory selected');

            await transliterateInventory(selectedInventory.id, selectedLanguageForTransliteration, inventoryTypes);

            const response = await monetisationPujaApi?.sankalpSheetApi?.getSankalpStoreDetails(String(storeId));
            setStoreDetails(response?.data);

            setSelectedLanguages(prev => ({
                ...prev,
                [activeTab]: selectedLanguageForTransliteration
            }));

            message.success(`Successfully transliterated to ${getLanguageDisplayName(selectedLanguageForTransliteration)}. It may take some time—try refreshing the page to view the changes.`);
        } catch (error) {
            showErrorToast(error)
            //message.error('Failed to transliterate');
            console.error('Transliteration error:', error);
        } finally {
            setLoading(false);
            setAvailableLanguagesForTransliteration([]);
            setSelectedLanguageForTransliteration("");
        }
    }, [activeTab, getSelectedInventory, getLanguageDisplayName, monetisationPujaApi, selectedLanguageForTransliteration, storeId, transliterateInventory]);

    const exportSankalpSheet = useCallback(async (payload: {
        store_id: string; type: string; transliterated_language: string; inventory_ids: string[]; export_type: string;
    }) => {
        try {
            const response = await monetisationPujaApi?.sankalpSheetApi?.exportSankalpSheet(payload);
            return response?.data;
        } catch (error) {
            console.error('Error exporting sankalp sheet:', error);
            throw error;
        }
    }, [monetisationPujaApi]);

    const handleExportModalOk = useCallback(async () => {
        const selectedIds = Object.keys(selectedInventories).filter(id => selectedInventories[id]);
        if (selectedIds.length === 0) {
            message.warning('Please select at least one inventory');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                store_id: storeId || '', type: activeTab, transliterated_language: exportLanguage,
                inventory_ids: selectedIds, export_type: exportFormat
            };

            await exportSankalpSheet(payload);
            message.success('Export initiated successfully');
            setIsExportModalVisible(false);
            setExportFormat("XLSX");
            setExportLanguage("default");
            setSelectedInventories({});
        } catch (error) {
            showErrorToast(error)
            //message.error('Failed to initiate export');
            console.error('Export error:', error);
        } finally {
            setLoading(false);
        }
    }, [activeTab, exportFormat, exportLanguage, exportSankalpSheet, selectedInventories, storeId]);

    const handleExportModalCancel = useCallback(() => {
        setIsExportModalVisible(false);
        setExportFormat("XLSX");
        setExportLanguage("default");
        setSelectedInventories({});
    }, []);

    const handleLanguageChange = useCallback((type: string, language: string) => {
        setSelectedLanguages(prev => ({ ...prev, [type]: language }));
    }, []);

    const handleInventoryChange = useCallback((type: string, inventoryId: string) => {
        setSelectedButtons(prev => ({ ...prev, [type]: inventoryId }));
        setCurrentPage(prev => ({ ...prev, [type]: 1 }));
        setTotalPages(prev => ({ ...prev, [type]: 0 }));

        const newInventory = groupedByType[type].find(inv => inv.id === inventoryId);
        if (newInventory?.transliterated_languages && newInventory.transliterated_languages.length > 0) {
            setSelectedLanguages(prev => ({ ...prev, [type]: newInventory.transliterated_languages![0] }));
        } else {
            setSelectedLanguages(prev => ({ ...prev, [type]: 'default' }));
        }
    }, [groupedByType]);

    const handlePageChange = useCallback((type: string, page: number) => {
        setCurrentPage(prev => ({ ...prev, [type]: page }));
    }, []);

    const handleTabChange = useCallback((activeKey: string) => {
        setActiveTab(activeKey);

        const newTypeInventories = groupedByType[activeKey] || [];
        if (newTypeInventories.length > 0) {
            const firstInventoryId = newTypeInventories[0].id;
            setSelectedButtons(prev => ({ ...prev, [activeKey]: firstInventoryId }));

            const firstInventory = newTypeInventories[0];
            if (firstInventory.transliterated_languages && firstInventory.transliterated_languages.length > 0) {
                setSelectedLanguages(prev => ({ ...prev, [activeKey]: firstInventory.transliterated_languages![0] }));
            } else {
                setSelectedLanguages(prev => ({ ...prev, [activeKey]: 'default' }));
            }
        }
    }, [groupedByType]);

    const handleInventorySelect = useCallback((inventoryId: string, checked: boolean) => {
        setSelectedInventories(prev => ({ ...prev, [inventoryId]: checked }));
    }, []);

    const getCurrentTypeInventories = useCallback(() => {
        const inventories = groupedByType[activeTab] || [];

        const filteredByLanguage = exportLanguage === 'default'
            ? inventories
            : inventories.filter(inv =>
                inv.transliterated_languages && inv.transliterated_languages.includes(exportLanguage)
            );

        return filteredByLanguage.filter(inv =>
            inv.inventory_type === 'item' || (inv.batch_ids && inv.batch_ids.length > 0)
        );
    }, [activeTab, exportLanguage, groupedByType]);

    const handleRowClick = useCallback((record: TableDataType, event: React.MouseEvent) => {
        const currentLanguage = selectedLanguages[activeTab] || 'default';
        if (currentLanguage === 'default') return;

        const target = event.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'BUTTON') return;

        setTableData(prevData =>
            prevData.map(item => ({
                ...item,
                isEditing: item.key === record.key ? !item.isEditing : false,
                editedGotra: item.key === record.key ? item.gotra : item.editedGotra,
                editedMemberNames: item.key === record.key
                    ? Object.keys(record).reduce((acc, key) => {
                        if (key.startsWith('member_')) acc[key] = record[key];
                        return acc;
                    }, {} as Record<string, string>)
                    : item.editedMemberNames
            }))
        );
    }, [activeTab, selectedLanguages]);

    const handleEditFieldChange = useCallback((key: string, fieldPath: string, value: string) => {
        setTableData(prevData =>
            prevData.map(item => {
                if (item.key !== key) return item;

                if (fieldPath.startsWith('editedMemberNames.')) {
                    const memberField = fieldPath.split('.')[1];
                    return {
                        ...item,
                        editedMemberNames: { ...item.editedMemberNames, [memberField]: value }
                    };
                } else {
                    return { ...item, [fieldPath]: value };
                }
            })
        );
    }, []);

    const handleUpdate = useCallback(async (record: TableDataType) => {
        try {
            if (!record.editedGotra || record.editedGotra.trim() === '') {
                message.error('Gotra cannot be empty');
                return;
            }

            const emptyMemberFields = [];
            const memberCount = Object.keys(record).filter(key => key.startsWith('member_')).length;
            for (let i = 0; i < memberCount; i++) {
                const memberName = record.editedMemberNames?.[`member_${i}`] || record[`member_${i}`] || '';
                if (!memberName || memberName.trim() === '') {
                    emptyMemberFields.push(`Member ${i + 1}`);
                }
            }

            if (emptyMemberFields.length > 0) {
                message.error(`The following member names cannot be empty: ${emptyMemberFields.join(', ')}`);
                return;
            }

            setLoading(true);

            const members = [];
            for (let i = 0; i < memberCount; i++) {
                members.push({
                    full_name: record.editedMemberNames?.[`member_${i}`] || record[`member_${i}`] || ''
                });
            }

            const payload = {
                batch_id: currentBatchDetails?.batchId || null,
                order_id: record.order_id,
                language_code: selectedLanguages[activeTab] || 'default',
                gotra: record.editedGotra || record.gotra,
                members: members
            };

            const selectedInventory = getSelectedInventory(activeTab);
            if (payload && selectedInventory?.id!) {
                await monetisationPujaApi?.sankalpSheetApi?.transliterateInventoryQc(
                    payload, selectedInventory?.id!
                );
            }

            message.success('Updated successfully');

            setTableData(prevData =>
                prevData.map(item => {
                    if (item.key !== record.key) return item;

                    const updatedItem: any = {
                        ...item, isEditing: false, gotra: item.editedGotra || item.gotra
                    };

                    Object.keys(item.editedMemberNames || {}).forEach(key => {
                        updatedItem[key] = item.editedMemberNames?.[key] || item[key];
                    });

                    return updatedItem;
                })
            );
        } catch (error) {
            console.error('Error updating sankalp details:', error);
            showErrorToast(error);
            //message.error('Failed to update sankalp details');
        } finally {
            setLoading(false);
        }
    }, [activeTab, currentBatchDetails?.batchId, monetisationPujaApi, selectedLanguages, getSelectedInventory]);

    const isSrimandir = app === A4B_APPS.SRIMANDIR;
    const getPageBreadcrumbs = useMemo(() => [
        {
            path: isSrimandir ? '/monetisation/puja/manage-sankalp-sheet' : '/astro/puja/puja-updates',
            breadcrumbName: 'Manage Sankalp Sheet',
        },
        { path: isSrimandir ? '/monetisation/puja' : '/astro/puja', breadcrumbName: 'Sankalp Sheet Details' },
    ], [isSrimandir]);
    const selectedInventory = getSelectedInventory(activeTab);
    const transliteratedLanguages = selectedInventory?.transliterated_languages;

    return (
        <div style={{ padding: "40px" }}>
            <AdminBreadcrumb routes={getPageBreadcrumbs} />

            <Container style={{ width: '100%' }}>
                <div>
                    <p style={{ fontWeight: 500, color: '#707070', fontSize: '12px' }}>
                        Store Id:
                        <span style={{ fontWeight: 600, color: '#969696', paddingLeft: '12px', fontSize: '14px' }}>
                            {storeId}
                        </span>
                    </p>
                    <p style={{ fontWeight: 500, color: '#707070', fontSize: '12px' }}>
                        Store Name:
                        <span style={{ fontWeight: 600, color: '#969696', paddingLeft: '12px', fontSize: '14px' }}>
                            {storeDetails?.data?.store_name}
                        </span>
                    </p>
                </div>
                <hr style={{ border: 'none', borderTop: '1px dashed #F0F0F0', backgroundColor: 'transparent', margin: '10px 0' }} />

                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
                    <Search
                        placeholder="Search by Booking Id" style={{ width: 200, marginRight: 10 }}
                        onSearch={handleSearch} onChange={(e) => setSearchValue(e.target.value)}
                        value={searchValue} allowClear
                    />
                    <div style={{ display: 'flex', gap: '10px' }}>
                        {getSelectedInventory(activeTab) && (
                            <>
                                <Select
                                    value={selectedLanguages[activeTab] || 'default'} style={{ width: 120 }}
                                    onChange={(value) => handleLanguageChange(activeTab, value)} placeholder="Select Language"
                                >
                                    <Option value="default">Default</Option>
                                    {getSelectedInventory(activeTab)?.transliterated_languages?.map((lang) => (
                                        <Option key={lang} value={lang}>{getLanguageDisplayName(lang)}</Option>
                                    ))}
                                </Select>

                                {(!transliteratedLanguages || transliteratedLanguages.length < 6) && (
                                    <Button type="primary" onClick={handleTranslate}>Transliterate</Button>
                                )}
                            </>
                        )}
                        <Button type="primary" onClick={handleExport}>Export</Button>
                    </div>
                </div>

                <StyledTabs activeKey={activeTab} onChange={handleTabChange} style={{ width: '100%' }}>
                    {Object.entries(groupedByType).map(([type, items]) => {
                        const selectedInventory = getSelectedInventory(type);
                        const currentLanguage = selectedLanguages[type] || 'default';

                        return (
                            <Tabs.TabPane key={type} tab={type === "DOMESTIC" ? "Domestic Puja" : "Global Puja"} style={{ width: '100%' }}>
                                <div style={{ marginBottom: 16 }}>
                                    <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center', flexWrap: 'wrap' }}>
                                        {items.slice(0, 8).map((inv) => (
                                            <Button
                                                key={inv.id} type={selectedButtons[type] === inv.id ? "primary" : "default"}
                                                onClick={() => handleInventoryChange(type, inv.id)} style={{ marginRight: 8, marginBottom: 8 }}
                                            >
                                                {inv.inventory_type === 'pooja' ? '🕉️ ' : '📦 '}{inv.name}
                                                {(inv.state === 'PROCESSING' || inv.state === 'ENQUEUED') && (<span style={{ marginLeft: 8 }}>⏳</span>)}
                                                {inv.state === 'FAILED' && (<span style={{ marginLeft: 8 }}>❌</span>)}
                                            </Button>
                                        ))}
                                    </div>

                                    {showAllItems && (
                                        <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
                                            {items.slice(8).map((inv) => (
                                                <Button
                                                    key={inv.id} type={selectedButtons[type] === inv.id ? "primary" : "default"}
                                                    onClick={() => handleInventoryChange(type, inv.id)} style={{ marginRight: 8, marginBottom: 8 }}
                                                >
                                                    {inv.inventory_type === 'pooja' ? '🕉️ ' : '📦 '}{inv.name}
                                                    {(inv.state === 'PROCESSING' || inv.state === 'ENQUEUED') && (<span style={{ marginLeft: 8 }}>⏳</span>)}
                                                    {inv.state === 'FAILED' && (<span style={{ marginLeft: 8 }}>❌</span>)}
                                                </Button>
                                            ))}
                                        </div>
                                    )}

                                    {items.length > 8 && (
                                        <Button
                                            type="text" onClick={() => setShowAllItems(!showAllItems)}
                                            style={{ marginRight: 8, marginBottom: 8, color: '#1890ff', border: '1px solid #1890ff' }}
                                        >
                                            {showAllItems ? 'Show Less' : `+${items.length - 8} More`}
                                        </Button>
                                    )}
                                </div>

                                {selectedInventory?.inventory_type === 'pooja' && (
                                    <div style={{ marginTop: 20, marginBottom: 40, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        {currentBatchDetails && (
                                            <div>Current Batch: {currentBatchDetails.batchId} ({currentBatchDetails.itemCount} items)</div>
                                        )}
                                        <div style={{ position: 'absolute', right: 20 }}>
                                            <Pagination
                                                current={currentPage[activeTab] || 1} total={(getSelectedInventory(activeTab)?.batch_ids?.length || 0)}
                                                pageSize={1} onChange={(page) => handlePageChange(activeTab, page)}
                                                showSizeChanger={false} showQuickJumper={true}
                                            />
                                        </div>
                                    </div>
                                )}

                                <Table
                                    columns={[
                                        ...tableColumns,
                                        ...(currentLanguage !== 'default' ? [{
                                            title: 'Action', key: 'action', fixed: 'right', width: 120,
                                            render: (_: any, record: any) => (
                                                record.isEditing ? (
                                                    <Button type="primary" onClick={(e) => { e.stopPropagation(); handleUpdate(record); }} loading={loading}>Update</Button>
                                                ) : (
                                                    <Button type="text" onClick={(e) => {
                                                        e.stopPropagation();
                                                        setTableData(prevData =>
                                                            prevData.map(item => ({
                                                                ...item,
                                                                isEditing: item.key === record.key ? true : false,
                                                                editedGotra: item.key === record.key ? item.gotra : item.editedGotra,
                                                                editedMemberNames: item.key === record.key
                                                                    ? Object.keys(record).reduce((acc, key) => {
                                                                        if (key.startsWith('member_')) acc[key] = record[key];
                                                                        return acc;
                                                                    }, {} as Record<string, string>)
                                                                    : item.editedMemberNames
                                                            }))
                                                        );
                                                    }}>Edit</Button>
                                                )
                                            ),
                                        }] : [])
                                    ]}
                                    dataSource={tableData} loading={loading} scroll={{ x: currentLanguage !== 'default' ? 2000 : 1500, y: 600 }}
                                    pagination={false} size="small"
                                    onRow={(record) => ({
                                        onClick: (event) => handleRowClick(record, event),
                                        style: { cursor: currentLanguage !== 'default' ? 'pointer' : 'default', backgroundColor: record.isEditing ? '#f0f9ff' : 'inherit' }
                                    })}
                                    locale={{ emptyText: searchValue ? `No bookings found for "${searchValue}"` : 'No data available' }}
                                />
                            </Tabs.TabPane>
                        );
                    })}
                </StyledTabs>
            </Container>

            <ExportModal
                visible={isExportModalVisible} onCancel={handleExportModalCancel} onOk={handleExportModalOk}
                exportFormat={exportFormat} setExportFormat={setExportFormat} exportLanguage={exportLanguage}
                setExportLanguage={setExportLanguage} inventories={getCurrentTypeInventories()}
                selectedInventories={selectedInventories} onInventorySelect={handleInventorySelect}
            />

            <TransliterateModal
                visible={isTransliterateModalVisible}
                onCancel={() => {
                    setIsTransliterateModalVisible(false);
                    setAvailableLanguagesForTransliteration([]);
                    setSelectedLanguageForTransliteration("");
                }}
                onOk={handleTransliterate} availableLanguages={availableLanguagesForTransliteration}
                selectedLanguage={selectedLanguageForTransliteration} setSelectedLanguage={setSelectedLanguageForTransliteration}
            />
        </div>
    );
};

export default SankalpSheetDetails;